// Process tree (DESIGN_SPEC §12.7, §10.10) from the event stream.
//   buildTree(events, opts?) → Tree (a ProcTree)   nodes keyed `${pid}:${startSeq}` (Windows reuses pids)
//   treeOf(ctx) → Tree                             memoized per job on snap.version · cite · artifacts · mode
//   nodeAt(tree, pid, seq) · parentOf(tree, node) · isConhostOnly(tree, node) · axisPct(tree, t)
// Nodes are the traced subtree (proc.start with scope ≠ false; the agent's own launch record
// {pid, target} counts). Untraced processes appear only as the root's context parent or as
// "external" ends of an injection. Times are ms from T0; `life` is % of the run axis
// (T0 → collection in a document, T0 → T0 + timeout while live).
import { envPath, winBase } from "@/lib/format";

/** The controller's rule (behavior.go): a thread created by the idle or System process (pid 0 or 4), or by a process in
 *  itself, is Windows lifecycle, not an injection. */
export const countsAsInjection = (creator: number | undefined, target: number | undefined): boolean =>
  !!creator && !!target && creator !== 4 && creator !== target;
import type { Artifact, Event, Severity } from "@/types";
import type { ActKind, CiteIndex, DumpRef, Injection, ProcNode, ProcTree, ReportCtx } from "@/lib/derive/types";

type BuildTreeOptions = {
  /** Epoch ms of T0 (stream.t0); null → the first event. */
  t0?: number | null;
  /** Severity of the tags citing each event (completed documents only; live passes nothing). */
  cite?: CiteIndex;
  /** Links dump.taken to its process-dump artifact by name. */
  artifacts?: readonly Artifact[];
  /** Live run: the axis runs T0 → T0 + timeout and processes without an exit are running. */
  live?: boolean;
  /** Epoch ms for running lifetimes (live); defaults to the newest event. */
  now?: number;
  timeoutMs?: number;
};

type YaraHit = { rule: string; artifact: string; on: "sample" | "dump" | "dropped" | "other"; seq: number; t: number };

export type TreeNode = ProcNode & {
  /** An injection end outside the traced subtree (never started in it). */
  external?: boolean;
  yaraHits: YaraHit[];
  /** The file event that wrote this node's image (dropped). */
  dropSeq?: number;
  /** parent_image as the agent reported it on proc.start. */
  parentImage?: string;
  /** Every event seq attributed to this node (start, exit, activity, injections, dumps, YARA). */
  seqs: number[];
};

export type Tree = Omit<ProcTree, "nodes" | "context"> & {
  nodes: ReadonlyMap<string, TreeNode>;
  context?: TreeNode;
  /** Depth-first display order (context first, then other roots, then external nodes). */
  order: string[];
  injections: Injection[];
  dumps: DumpRef[];
  /** Traced processes (excludes the context and external nodes). */
  inScope: number;
  /** Epoch ms the node times are relative to. */
  t0: number;
};

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

function num(v: unknown): number | undefined {
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Case- and %ENV%-insensitive path key. */
export const pathKey = (p: string): string => envPath(p.trim().replace(/\//g, "\\")).toLowerCase().replace(/\\+$/, "");

/** Thread start address as hex (the agent sends a number, the mock a hex string). */
export function hexAddr(v: unknown): string | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return `0x${v.toString(16)}`;
  const s = str(v).trim();
  if (!s) return undefined;
  if (/^\d+$/.test(s)) {
    try {
      return `0x${BigInt(s).toString(16)}`;
    } catch {
      return s;
    }
  }
  return s;
}

/** argv[0] and the rest of a Windows command line. */
export function splitArgv0(cmd: string): [string, string] {
  const s = cmd.trim();
  if (s.startsWith('"')) {
    const end = s.indexOf('"', 1);
    return end > 0 ? [s.slice(1, end), s.slice(end + 1).trim()] : [s.slice(1), ""];
  }
  const sp = s.search(/\s/);
  return sp < 0 ? [s, ""] : [s.slice(0, sp), s.slice(sp + 1).trim()];
}

/** Arguments only when argv[0] is the image itself, else the full command line. */
function argsOf(cmdline: string, image: string): string {
  if (!cmdline) return "";
  const [argv0, rest] = splitArgv0(cmdline);
  return image && argv0.toLowerCase() === image.toLowerCase() ? rest : cmdline;
}

const ACT: Partial<Record<string, ActKind>> = {
  "image.load": "load",
  "file.create": "file",
  "file.write": "file",
  "file.delete": "file",
  "file.rename": "file",
  "file.drop": "file",
  "registry.set": "reg",
  "registry.delete": "reg",
  "mutex.create": "mutex",
  "net.conn": "net",
  "net.dns": "net",
  "net.http": "net",
  "net.flow": "net",
};

const emptyActs = (): Record<ActKind, number> => ({ load: 0, file: 0, reg: 0, net: 0, mutex: 0 });

function blankNode(key: string, pid: number, ppid: number, image: string, startSeq: number, startT: number): TreeNode {
  return {
    key,
    pid,
    ppid,
    image,
    name: image ? winBase(image) : "",
    cmdline: "",
    args: "",
    inScope: true,
    startSeq,
    startT,
    children: [],
    depth: 0,
    injectedBy: [],
    injectedInto: [],
    dumps: [],
    yara: [],
    acts: emptyActs(),
    life: { a: 0, b: 0 },
    running: false,
    yaraHits: [],
    seqs: [],
  };
}

export function buildTree(events: readonly Event[], opts: BuildTreeOptions = {}): Tree {
  const firstTs = events.length ? Date.parse(events[0].ts) : NaN;
  const t0 = opts.t0 ?? (Number.isFinite(firstTs) ? firstTs : 0);
  const tOf = (e: Event) => {
    const ms = Date.parse(e.ts);
    return Number.isFinite(ms) ? ms - t0 : 0;
  };

  const nodes = new Map<string, TreeNode>();
  const byPid = new Map<number, string[]>();
  const roots: string[] = [];
  const untraced = new Map<number, string>(); // pid → image, from scope:false proc.start
  const writes = new Map<string, { pid: number; seq: number; t: number }>(); // first writer per path
  const injections: Injection[] = [];
  const dumps: DumpRef[] = [];
  const yaraEvents: { rule: string; artifact: string; kind: string; seq: number; t: number }[] = [];
  let collectT: number | undefined;
  let endT: number | undefined;
  let lastT = 0;

  const nodeAtPid = (pid: number, seq: number): TreeNode | undefined => {
    const keys = byPid.get(pid);
    if (!keys) return undefined;
    for (let i = keys.length - 1; i >= 0; i--) {
      const n = nodes.get(keys[i])!;
      if (n.startSeq <= seq) return n;
    }
    return undefined;
  };

  for (const e of events) {
    const f = e.fields ?? {};
    const t = tOf(e);
    if (t > lastT) lastT = t;
    switch (e.kind) {
      case "job.state": {
        const s = str(f.state);
        if (s === "collecting" && collectT === undefined) collectT = t;
        if ((s === "completed" || s === "failed" || s === "cancelled") && endT === undefined) endT = t;
        break;
      }
      case "proc.start": {
        const pid = num(f.pid);
        if (!pid) break;
        const image = str(f.image);
        if (f.scope === false) {
          if (image) untraced.set(pid, image);
          break;
        }
        // A live node for this pid already (the agent's launch record next to the driver's start): merge.
        const live = nodeAtPid(pid, e.seq);
        if (live && live.endSeq === undefined) {
          if (!live.image && image) {
            live.image = image;
            live.name = winBase(image);
          }
          if (!live.cmdline && str(f.cmdline)) live.cmdline = str(f.cmdline);
          if (!live.ppid && num(f.ppid)) live.ppid = num(f.ppid)!;
          if (!live.parentImage && str(f.parent_image)) live.parentImage = str(f.parent_image);
          live.seqs.push(e.seq);
          break;
        }
        const ppid = num(f.ppid) ?? 0;
        const node = blankNode(`${pid}:${e.seq}`, pid, ppid, image, e.seq, t);
        node.cmdline = str(f.cmdline);
        node.parentImage = str(f.parent_image) || undefined;
        node.seqs.push(e.seq);
        const parent = ppid ? nodeAtPid(ppid, e.seq) : undefined;
        if (parent) {
          node.parentKey = parent.key;
          parent.children.push(node.key);
        } else {
          roots.push(node.key);
        }
        nodes.set(node.key, node);
        byPid.set(pid, [...(byPid.get(pid) ?? []), node.key]);
        break;
      }
      case "proc.exit": {
        const pid = num(f.pid);
        const n = pid ? nodeAtPid(pid, e.seq) : undefined;
        if (!n || n.endSeq !== undefined) break;
        n.endSeq = e.seq;
        n.endT = t;
        const code = num(f.exit_code);
        if (code !== undefined) n.exitCode = code;
        if (!n.image && str(f.image)) {
          n.image = str(f.image);
          n.name = winBase(n.image);
        }
        n.seqs.push(e.seq);
        break;
      }
      case "thread.inject": {
        const creatorPid = num(f.creator_pid);
        const targetPid = num(f.target_pid);
        if (!creatorPid || !targetPid || !countsAsInjection(creatorPid, targetPid)) break;
        const inj: Injection = { creatorPid, targetPid, t, seq: e.seq };
        const tid = num(f.tid);
        if (tid !== undefined) inj.tid = tid;
        const addr = hexAddr(f.start_addr);
        if (addr) inj.startAddr = addr;
        injections.push(inj);
        if (str(f.creator_image) && !untraced.has(creatorPid)) untraced.set(creatorPid, str(f.creator_image));
        if (str(f.target_image) && !untraced.has(targetPid)) untraced.set(targetPid, str(f.target_image));
        break;
      }
      case "dump.taken": {
        const pid = num(f.pid);
        if (!pid) break;
        const d: DumpRef = { pid, t, seq: e.seq };
        if (str(f.name)) d.name = str(f.name);
        if (str(f.reason)) d.reason = str(f.reason);
        const size = num(f.size);
        if (size !== undefined) d.size = size;
        dumps.push(d);
        break;
      }
      case "yara.match":
        yaraEvents.push({ rule: str(f.rule), artifact: str(f.artifact), kind: str(f.kind), seq: e.seq, t });
        break;
      default:
        break;
    }

    // activity counts (+ first writer of each path, for "dropped by")
    const act = ACT[e.kind];
    if (act) {
      const pid = num(f.pid);
      const n = pid ? nodeAtPid(pid, e.seq) : undefined;
      if (n) {
        n.acts[act]++;
        n.seqs.push(e.seq);
        if (act === "load" && !n.image) {
          const img = str(f.image);
          if (/\.exe$/i.test(img)) {
            n.image = img;
            n.name = winBase(img);
          }
        }
      }
      if (pid && (e.kind === "file.create" || e.kind === "file.write" || e.kind === "file.drop" || e.kind === "file.rename")) {
        const p = str(e.kind === "file.rename" ? f.to : f.path);
        if (p) {
          const k = pathKey(p);
          if (!writes.has(k)) writes.set(k, { pid, seq: e.seq, t });
        }
      }
    }
  }

  // Images the agent never reported (its launch record): borrow from injections.
  for (const n of nodes.values()) {
    if (!n.image && untraced.has(n.pid)) {
      n.image = untraced.get(n.pid)!;
      n.name = winBase(n.image);
    }
    n.args = argsOf(n.cmdline, n.image);
  }

  // Dropped: the image was written during this run before the process started.
  for (const n of nodes.values()) {
    if (!n.image) continue;
    const w = writes.get(pathKey(n.image));
    if (w && w.seq < n.startSeq) {
      n.dropped = { byPid: w.pid, t: w.t };
      n.dropSeq = w.seq;
      n.seqs.push(w.seq);
    }
  }

  // Context: the untraced parent of the first root, shown unexpanded above it.
  let context: TreeNode | undefined;
  const first = roots.length ? nodes.get(roots[0]) : undefined;
  if (first) first.isSample = true;
  if (first && first.ppid > 0) {
    const image = first.parentImage || untraced.get(first.ppid) || "";
    context = blankNode(`ctx:${first.ppid}`, first.ppid, 0, image, 0, 0);
    context.isContext = true;
    context.inScope = false;
    context.children = roots.filter((k) => nodes.get(k)!.ppid === first.ppid);
  }

  // External ends of injections (the target or creator was never started inside the subtree).
  const external = new Map<number, TreeNode>();
  const endpoint = (pid: number, seq: number): TreeNode => {
    const n = nodeAtPid(pid, seq);
    if (n) return n;
    if (context && context.pid === pid) return context;
    let x = external.get(pid);
    if (!x) {
      x = blankNode(`ext:${pid}`, pid, 0, untraced.get(pid) ?? "", seq, 0);
      x.external = true;
      x.inScope = false;
      external.set(pid, x);
    }
    return x;
  };
  for (const inj of injections) {
    const creator = endpoint(inj.creatorPid, inj.seq);
    const target = endpoint(inj.targetPid, inj.seq);
    creator.injectedInto.push(inj);
    target.injectedBy.push(inj);
    creator.seqs.push(inj.seq);
    if (target !== creator) target.seqs.push(inj.seq);
  }

  // Dumps → their process (and the process-dump artifact by name).
  const dumpArtifacts = new Map<string, Artifact>();
  for (const a of opts.artifacts ?? []) if (a.kind === "process-dump") dumpArtifacts.set(a.name.toLowerCase(), a);
  for (const d of dumps) {
    if (d.name) {
      const a = dumpArtifacts.get(d.name.toLowerCase());
      if (a) d.artifact = a;
    }
    const n = nodeAtPid(d.pid, d.seq) ?? (context?.pid === d.pid ? context : external.get(d.pid));
    if (n) {
      n.dumps.push(d);
      n.seqs.push(d.seq);
    }
  }

  // YARA: on the sample → the root; on a dump → the dumped process; on a dropped file → the process started from it.
  for (const y of yaraEvents) {
    const art = y.artifact.toLowerCase();
    let n: TreeNode | undefined;
    let on: YaraHit["on"] = "other";
    const dump = dumps.find((d) => d.name && d.name.toLowerCase() === art);
    if (y.kind === "sample" || (!dump && first && first.name.toLowerCase() === art && y.kind !== "dropped-file")) {
      n = first;
      on = "sample";
    } else if (dump) {
      n = nodeAtPid(dump.pid, dump.seq) ?? external.get(dump.pid);
      on = "dump";
    } else {
      n = [...nodes.values()].find((x) => x.dropped && x.name.toLowerCase() === art);
      on = "dropped";
    }
    if (!n) continue;
    n.yaraHits.push({ rule: y.rule, artifact: y.artifact, on, seq: y.seq, t: y.t });
    if (!n.yara.includes(y.rule)) n.yara.push(y.rule);
    n.seqs.push(y.seq);
  }

  // Run axis.
  const traced = [...nodes.values()];
  const minStart = traced.reduce((m, n) => Math.min(m, n.startT), 0);
  const nowT = opts.now !== undefined ? opts.now - t0 : lastT;
  let to: number;
  if (opts.live) to = Math.max(opts.timeoutMs ?? 0, nowT, lastT);
  else to = collectT ?? endT ?? lastT;
  const from = Math.min(0, minStart);
  if (!(to > from)) to = from + 1000;
  const pct = (t: number) => Math.max(0, Math.min(100, ((t - from) / (to - from)) * 100));

  for (const n of traced) {
    n.running = !!opts.live && n.endT === undefined;
    n.life = { a: pct(n.startT), b: n.endT !== undefined ? pct(n.endT) : opts.live ? pct(nowT) : 100 };
    if (opts.cite) {
      let sev: Severity | undefined;
      for (const s of n.seqs) {
        const c = opts.cite.get(s);
        if (c && (!sev || c.sev > sev)) sev = c.sev;
      }
      if (sev) n.badgeSev = sev;
    }
  }

  // Depth and display order.
  const order: string[] = [];
  const all = new Map<string, TreeNode>(nodes);
  const walk = (key: string, depth: number) => {
    const n = all.get(key);
    if (!n) return;
    n.depth = depth;
    order.push(key);
    for (const c of n.children) walk(c, depth + 1);
  };
  if (context) {
    all.set(context.key, context);
    context.depth = 0;
    order.push(context.key);
    for (const k of context.children) walk(k, 1);
    for (const k of roots) if (!context.children.includes(k)) walk(k, 0);
  } else {
    for (const k of roots) walk(k, 0);
  }
  for (const x of external.values()) {
    all.set(x.key, x);
    order.push(x.key);
  }

  return {
    nodes: all,
    roots,
    context,
    byPid,
    axis: { from, to },
    order,
    injections,
    dumps,
    inScope: nodes.size,
    t0,
  };
}

// ---- lookups ------------------------------------------------------------------------

/** A T0-relative time as a % of the tree's run axis (clamped). */
export function axisPct(tree: Pick<Tree, "axis">, t: number): number {
  const { from, to } = tree.axis;
  return to > from ? Math.max(0, Math.min(100, ((t - from) / (to - from)) * 100)) : 0;
}

/** The node for `pid` that was alive at `seq` (latest started at or before it). */
export function nodeAt(tree: Tree, pid: number, seq: number): TreeNode | undefined {
  const keys = tree.byPid.get(pid);
  if (keys) {
    for (let i = keys.length - 1; i >= 0; i--) {
      const n = tree.nodes.get(keys[i]);
      if (n && n.startSeq <= seq) return n;
    }
  }
  if (tree.context?.pid === pid) return tree.context;
  return tree.nodes.get(`ext:${pid}`);
}

/** Parent chain, nearest first (the context counts as the root's parent). */
export function parentOf(tree: Tree, n: TreeNode): TreeNode | undefined {
  if (n.parentKey) return tree.nodes.get(n.parentKey);
  if (tree.context && !n.isContext && !n.external && tree.context.children.includes(n.key)) return tree.context;
  return undefined;
}

/** A node whose only children are console hosts starts collapsed (DESIGN_SPEC §10.10). */
export function isConhostOnly(tree: Tree, n: TreeNode): boolean {
  return n.children.length > 0 && n.children.every((k) => tree.nodes.get(k)?.name.toLowerCase() === "conhost.exe");
}

// ---- memo per report --------------------------------------------------------------------

type Memo = { version: number; cite: CiteIndex; artifacts: readonly Artifact[]; mode: string; t0: number | null; timeout: number; tree: Tree };
const memo = new Map<string, Memo>();

export function treeOf(ctx: ReportCtx): Tree {
  const id = ctx.job.id;
  const timeout = (ctx.job.timeout_secs || 0) * 1000;
  const m = memo.get(id);
  if (
    m &&
    m.version === ctx.stream.snap.version &&
    m.cite === ctx.cite &&
    m.artifacts === ctx.artifacts &&
    m.mode === ctx.mode &&
    m.t0 === ctx.stream.t0 &&
    m.timeout === timeout
  )
    return m.tree;
  const live = ctx.mode === "live";
  const tree = buildTree(ctx.stream.snap.events, {
    t0: ctx.stream.t0,
    cite: live ? undefined : ctx.cite,
    artifacts: ctx.artifacts,
    live,
    timeoutMs: timeout,
  });
  memo.delete(id);
  memo.set(id, { version: ctx.stream.snap.version, cite: ctx.cite, artifacts: ctx.artifacts, mode: ctx.mode, t0: ctx.stream.t0, timeout, tree });
  if (memo.size > 8) memo.delete(memo.keys().next().value!);
  return tree;
}
